import { Fragment } from 'react';
import Image from 'next/image';

import { BodyRegular, BodySmall } from '@common/body';
import { H3, H4, H5 } from '@common/headline';
import { Overline } from '@common/overline';
import { EmojiIcon } from '../EmojiIcon';
import { TakeOurQuizLink } from '../Hero/TakeOurQuizCTA';
import PenSmall from '@common/Icons/PenSmall';

import ellipse from '@public/img/medically-supported/ellipse-white.png';
import starOne from '@public/img/medically-supported/star-1.png';
import starTwo from '@public/img/medically-supported/star-2.png';

type EmojiWithTextProps = {
  emoji: JSX.Element;
  supertitle: string;
  title: string;
  body: string;
  linkToQuiz?: boolean;
  utmCampaign?: string;
  utmMedium?: string;
  utmSource?: string;
};

const emojiTiles: EmojiWithTextProps[] = [
  {
    emoji: <p>&#x1F5D2;</p>, // 🗒
    title: 'Take your quiz',
    supertitle: 'Step 1',
    body: 'The clinical team will check your eligibility and suggest a programme tailored to you',
    linkToQuiz: true,
  },
  {
    emoji: <p>&#x1F4E6;</p>, // 📦
    title: 'Receive your medication',
    supertitle: 'Step 2',
    body: 'Delivered discreetly through your door, with our hardback recipe book and smart weighing scales',
  },
  {
    emoji: <>&#x1F933;</>, // 🤳
    title: 'Download the app',
    supertitle: 'Step 3',
    body: 'Retrain your mind from just 5-10 mins a day with our science-based articles and 1-to-1 coach support',
  },
  {
    emoji: <>&#x1F9D8;&#x200D;&#x2640;&#xFE0F;</>, // 🧘‍♀️
    title: 'Enjoy lifelong changes',
    supertitle: 'Step 4',
    body: 'Alongside weight loss, our members report improved mood, sleep, energy, and confidence',
  },
];

const EmojiWithText = ({
  emoji,
  title,
  body,
  supertitle,
  linkToQuiz,
  utmCampaign,
  utmMedium,
  utmSource,
}: EmojiWithTextProps): JSX.Element => {
  const ConditionalLink = ({ children }: { children: JSX.Element }) =>
    linkToQuiz ? (
      <TakeOurQuizLink
        location="Medically supported how-it-works"
        utmCampaign={utmCampaign}
        utmMedium={utmMedium}
        utmSource={utmSource}
      >
        {children}
      </TakeOurQuizLink>
    ) : (
      children
    );

  return (
    // z-index makes sure the emoji sits over the horizontal dashed line
    <div className="z-[1] flex flex-col items-center text-center lg:w-[224px]">
      <ConditionalLink>
        <EmojiIcon emoji={emoji} />
      </ConditionalLink>
      <div className="mt-5 w-full uppercase text-dawn-blue">
        <Overline>{supertitle}</Overline>
      </div>
      <ConditionalLink>
        <>
          <div className="mt-1 w-full text-blue lg:hidden">
            <H4>{title}</H4>
          </div>
          <div className="mt-1 hidden w-full text-blue lg:block">
            <H5>{title}</H5>
          </div>
        </>
      </ConditionalLink>
      <div className="mt-2 w-full text-blue-800 lg:hidden">
        <BodyRegular>{body}</BodyRegular>
      </div>
      <div className="mt-2 hidden w-full text-blue-800 lg:block">
        <BodySmall>{body}</BodySmall>
      </div>
    </div>
  );
};

const MedicationGuarantee = (): JSX.Element => (
  <div
    className="m-auto mt-16 max-w-[436px] rounded-2xl p-4 text-center text-blue"
    style={{
      background:
        'linear-gradient(90deg, rgba(255, 214, 69, 0.6) 0%, rgba(255, 225, 120, 0.6) 100%)',
    }}
  >
    <div className="relative mb-3 flex flex-row justify-center">
      <div className="absolute right-[80px] top-[-10px]">
        <Image src={starOne} height={14} width={14} alt="Star" />
      </div>
      <div className="absolute left-[45px] top-[20px]">
        <Image src={starTwo} height={10} width={10} alt="Star" />
      </div>
      <div className="relative mr-2">
        <Image src={ellipse} height={21} width={21} alt="Ellipse" />
        <div className="absolute left-[-2px] top-[-1px]">
          <PenSmall />
        </div>
      </div>
      <H4>Medication guarantee</H4>
    </div>
    <BodyRegular>
      You{"'"}ll never need to worry about medication supply issues. We cap the
      number of members joining each week, giving you peace of mind that you
      {"'"}ll get your medication each month.
    </BodyRegular>
  </div>
);

export const MedicationSupportedHowItWorks = (): JSX.Element => (
  <div className="relative w-full">
    <div className="absolute top-[256px] hidden w-full border-t-2 border-dashed border-mono-white lg:block" />
    <div className="flex w-full flex-col justify-center bg-dawn-blue-100 px-3 pb-16 pt-[72px] lg:px-[60px] lg:pb-[152px] lg:pt-28">
      <div className="flex w-[351px] flex-col items-center justify-center lg:w-full">
        <div className="flex text-center text-blue">
          <H3>
            How <span className="text-teal">it works</span>
          </H3>
        </div>
        <div className="mt-2 flex w-[343px] flex-col items-center justify-center gap-y-2 lg:mt-20 lg:w-full lg:max-w-[1120px] lg:flex-row lg:justify-between">
          {emojiTiles.map(tile => (
            <Fragment key={tile.title}>
              <div className="left-[50%] h-7 border-l-2 border-dashed border-mono-white lg:hidden" />
              <EmojiWithText key={tile.title} {...tile} />
            </Fragment>
          ))}
        </div>
      </div>
      <MedicationGuarantee />
    </div>
  </div>
);
